import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import _ from 'lodash'
import { getAffectedSide, getEtiologiesFromSite, getMapping, getWeaknessTypeFromSiteAndEtiology, NeurologicWeaknessItem, WeaknessType } from '@fusion-advantage/formlib'
import { L2TabularListItemSummary } from '../l2/L2TabularListItem'
import { L2TabularListContent, L2TabularListHeader, L2TabularListRoot } from '../l2/L2TabularList'
import { L1PlainIconButton, L1ReasonableButton } from '../l1/L1Button'
import L2ControlGrid from '../l2/L2ControlGrid'
import L2ControlGridLayout from '../l2/L2ControlGridLayout'
import { L2Dialog, L2DialogActions, L2DialogContent } from '../l2/L2Dialog'
import L1Boolean from '../l1/L1Boolean'
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons'
import { FusionColor } from '../../../theme/FusionStyle'

type L3NeurologicWeaknessListProps = {
	models: NeurologicWeaknessItem[],
	currentModel: NeurologicWeaknessItem|null,
	onChange: (e: NeurologicWeaknessItem[]) => void,
	selectedSite: string|null,
	showDialog: boolean,
	onHideDialog: () => void,
}

const L3NeurologicWeaknessListContainer = styled.div(({theme}) => ({
	boxSizing: 'border-box',
	padding: 0,
	display: 'flex',
	gap: theme.spacing(3),
	flexDirection: 'column',
}))

const DialogContentContainer = styled.div(({theme}) => ({
	padding: 0,
	width: '100%',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-around',
	alignItems: 'flex-start',
}))

type AddNeurologicWeaknessDialogProps = {
	currentModel: NeurologicWeaknessItem|null,
	site: string,
	onSave: (etiology: string, type: string) => void,
	onClose: () => void,
	show: boolean
}

const AddNeurologicWeaknessDialog = ({currentModel, site, onSave = () => {}, show, onClose}: AddNeurologicWeaknessDialogProps) => {
	const [etiology, setEtiology] = useState(currentModel?.etiology || '')
	const [type, setType] = useState(currentModel?.type || '')

	const validWeaknessTypes = useMemo(() => {
		return getWeaknessTypeFromSiteAndEtiology(site, etiology)
	}, [etiology, site])

	useEffect(() => {
		if(type && !validWeaknessTypes.includes(type as WeaknessType)) {
			setType('')
		}
	}, [validWeaknessTypes])

	const handleClose = () => {
		setEtiology('')
		setType('')

		onClose()
	}

	const handleSave = () => {
		if(!etiology || !type) return

		setEtiology('')
		setType('')

		onSave(etiology, type)
	}

	return (
		<L2Dialog
			show={show}
			onClose={handleClose}
			title='Add Etiology And Type'
			size='min'
		>
			<L2DialogContent>
				<DialogContentContainer>
					<L2ControlGrid layout={L2ControlGridLayout.FlexColumn}>
						<label>Select Etiology</label>				
						{
							getEtiologiesFromSite(site).map(mEtiology => (
								<L1Boolean key={mEtiology} checked={etiology === mEtiology} inputType='radio' name={mEtiology} label={mEtiology} onChange={(e) => setEtiology(mEtiology)} />
							))
						}
					</L2ControlGrid>
					<L2ControlGrid layout={L2ControlGridLayout.FlexColumn}>
						<label>Select Type</label>
						{
							validWeaknessTypes.map(mType => (
								<L1Boolean key={mType} checked={type === mType} inputType='radio' name={mType} label={mType} onChange={(e) => setType(mType)} />
							))
						}
					</L2ControlGrid>
				</DialogContentContainer>
			</L2DialogContent>
			<L2DialogActions>
				<L1ReasonableButton
					disabled={!etiology || !type}
					label='Save'
					onClick={handleSave}
					wide={true}
				/>
			</L2DialogActions>
		</L2Dialog>
	)
}

const L3NeurologicWeaknessList = ({showDialog, selectedSite, models, currentModel, onChange, onHideDialog}: L3NeurologicWeaknessListProps) => {
	const currentModelIdx = useMemo(() => {
		return models.findIndex(x => x === currentModel)
	}, [models, currentModel])

	const handleRemoveModel = (idx: number) => {
		const modelsTemp = _.cloneDeep(models)
		modelsTemp.splice(idx, 1)

		onChange(modelsTemp)
	}

	const handleSave = (etiology: string, type: string) => {
		if(selectedSite) {
			const modelsTemp = _.cloneDeep(models)
			const match = getMapping(selectedSite, etiology, type)

			if(currentModel && match && currentModelIdx > -1) {
				modelsTemp[currentModelIdx] = match
			}
			else if(match) {
				modelsTemp.push(match)
			}

			onChange([...modelsTemp])
		}

		onHideDialog()
	}

	return (
		<>
		{
			showDialog && <AddNeurologicWeaknessDialog currentModel={currentModel} site={selectedSite!} onSave={handleSave} onClose={onHideDialog} show={showDialog} />
		}
		<L3NeurologicWeaknessListContainer>
			<L2TabularListRoot columnCount={4}>
				<L2TabularListHeader>
					<div>Site</div>
					<div>Type</div>
					<div>Etiology</div>
				</L2TabularListHeader>
				<L2TabularListContent>
				{
					models.map((model, idx) => (
						<L2TabularListItemSummary
							key={idx}
						>
							<div>{model.site}</div>
							<div>{model.type}</div>
							<div>{model.etiology}</div>
							<L1PlainIconButton
								icon={faTrashCan}
								color={FusionColor.StopRed}
								onClick={() => handleRemoveModel(idx)}
							/>
						</L2TabularListItemSummary>
					))
				}
				</L2TabularListContent>
			</L2TabularListRoot>
		</L3NeurologicWeaknessListContainer>
		</>
	)
}

export default L3NeurologicWeaknessList